import { Column, Entity, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { CatalogValueEntity } from './catalog-value.entity';
import { LocationEntity } from './location.entity';

/**
 * Kind of vehicle. Extensible — trucks today; other types (e.g. trailer,
 * van) may follow without schema changes.
 */
export enum VehicleType {
	TRUCK = 'truck',
}

/**
 * Commercial driver class. Drives which DOT pre/post-trip inspection
 * sections apply — e.g. the trailer / coupling-devices sections are
 * Class-A-only and are hidden for Class B straight trucks (PD-13743).
 */
export enum VehicleClass {
	CLASS_A = 'A',
	CLASS_B = 'B',
}

/**
 * Operational status of a vehicle — vehicle-owned, NOT the location's status.
 * Vehicles previously borrowed LocationStatus (where maintenance/offline were
 * really equipment statuses being abused on the location). Vehicles now carry
 * their own status; the location keeps only its active/inactive meaning.
 */
export enum VehicleStatus {
	ACTIVE = 'active',
	INACTIVE = 'inactive',
	MAINTENANCE = 'maintenance',
	OFFLINE = 'offline',
}

/**
 * A physical fleet vehicle (truck).
 *
 * Previously a "vehicle" was modeled only as a `LocationEntity` row with
 * `type='vehicle'` — which meant vehicle-specific attributes (class, VIN,
 * unit #, …) had to pile onto the location table, and `Trip.vehicleId` was
 * literally a location id. PD-13767 makes the vehicle a real entity that
 * OWNS one or more child locations (`location.fk_vehicle__id`).
 *
 * Inventory for the vehicle lives at its DESIGNATED PRIMARY location
 * (`fk_location__id_primary`) — the single resolver target for every
 * truck-inventory operation. When only one child location is mapped, that
 * sole location is treated as primary (covers pre-existing data, where each
 * vehicle backfills to exactly one location).
 */
@Entity('vehicle')
export class VehicleEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('enum', {
		name: 'type',
		enum: VehicleType,
		default: VehicleType.TRUCK,
	})
	public type!: VehicleType;

	@Column('enum', { name: 'class', enum: VehicleClass, nullable: true })
	public vehicleClass!: VehicleClass | null;

	/** Manufacturer's 17-char vehicle identification number (globally unique). */
	@Column('varchar', { name: 'vin', nullable: true, length: 32 })
	public vin!: string | null;

	/**
	 * Company's internal fleet number (e.g. "Unit 12" / "Truck 7") — the
	 * identifier dispatchers/drivers use day-to-day. Distinct from the VIN.
	 * Requested by the customer on PD-13767.
	 */
	@Column('varchar', { name: 'unitNumber', nullable: true, length: 64 })
	public unitNumber!: string | null;

	/** Basic vehicle info: year. Make/model are managed-catalog FKs below (PD-14338). */
	@Column('smallint', { name: 'year', unsigned: true, nullable: true })
	public year!: number | null;

	/** Managed-catalog make (catalog_value, type vehicle_make). PD-14338. */
	@Column('int', {
		name: 'fk_catalog_value__id_make',
		unsigned: true,
		nullable: true,
	})
	public makeId!: number | null;

	/**
	 * Managed-catalog model (catalog_value, type vehicle_model). Its catalog
	 * value's parent is the chosen make. PD-14338.
	 */
	@Column('int', {
		name: 'fk_catalog_value__id_model',
		unsigned: true,
		nullable: true,
	})
	public modelId!: number | null;

	@ManyToOne(() => CatalogValueEntity, { nullable: true })
	@JoinColumn({ name: 'fk_catalog_value__id_make' })
	public makeCatalog!: CatalogValueEntity | null;

	@ManyToOne(() => CatalogValueEntity, { nullable: true })
	@JoinColumn({ name: 'fk_catalog_value__id_model' })
	public modelCatalog!: CatalogValueEntity | null;

	@Column('varchar', { name: 'description', nullable: true, length: 255 })
	public description!: string | null;

	/**
	 * License plate — a true vehicle attribute (was stored on the vehicle's
	 * location row as location.code). Capacity / volumeUnit intentionally stay
	 * on the LOCATION (inventory/location concepts), as does the location's own
	 * active/inactive status.
	 */
	@Column('varchar', { name: 'licensePlate', nullable: true, length: 50 })
	public licensePlate!: string | null;

	@Column('enum', {
		name: 'status',
		enum: VehicleStatus,
		default: VehicleStatus.ACTIVE,
	})
	public status!: VehicleStatus;

	/**
	 * Designated primary inventory location — the location truck-inventory
	 * operations read from / write to. Nullable; when null and the vehicle
	 * has exactly one child location, that sole location is the primary.
	 */
	@Column('int', {
		name: 'fk_location__id_primary',
		unsigned: true,
		nullable: true,
	})
	public primaryLocationId!: number | null;

	/**
	 * Last-known GPS position of the vehicle (PD-13913). GPS belongs to the
	 * thing that moves — the truck/driver — NOT to the inventory location
	 * (locations are subordinate to an address that already has a geocode). A
	 * position-bearing driver action (stop arrival/completion, pre/post-trip
	 * inspection) captures the device GPS and bubbles it up here (and to the
	 * acting user — see UserEntity). The live vehicle map + Driver Map read
	 * these; `currentLocationAt` drives the "updated N min ago" staleness pill.
	 */
	@Column('decimal', {
		name: 'currentLatitude',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public currentLatitude!: string | null;

	@Column('decimal', {
		name: 'currentLongitude',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public currentLongitude!: string | null;

	@Column('datetime', { name: 'currentLocationAt', nullable: true })
	public currentLocationAt!: Date | null;

	/**
	 * Which action recorded the latest position (stop_arrival / stop_complete /
	 * checkout / checkin / manual).
	 */
	@Column('varchar', {
		name: 'currentLocationSource',
		length: 32,
		nullable: true,
	})
	public currentLocationSource!: string | null;

	@ManyToOne(() => LocationEntity)
	@JoinColumn({ name: 'fk_location__id_primary' })
	public fkPrimaryLocation!: LocationEntity | null;

	// ── Motive telematics integration (PD-14140) ────────────────────────
	// A vehicle can be paired 1:1 with a vehicle in the tenant's Motive
	// (gomotive.com) fleet. We link by Motive's VEHICLE id — not the ELD
	// device id — because devices are hardware that gets swapped between
	// trucks, while the Motive vehicle is the stable logical object every
	// useful Motive API (locations, image capture, video recall) is keyed
	// by. Pairing happens automatically at import or manually in the
	// vehicle editor.

	/** Motive vehicle id this vehicle is paired to (unique per tenant). */
	@Column('bigint', { name: 'motiveVehicleId', nullable: true })
	public motiveVehicleId!: string | null;

	/** When the Motive sync last wrote telemetry for this vehicle. */
	@Column('datetime', { name: 'motiveSyncedAt', nullable: true })
	public motiveSyncedAt!: Date | null;

	/** Fuel type as reported by Motive (e.g. diesel, gasoline). */
	@Column('varchar', { name: 'fuelType', length: 32, nullable: true })
	public fuelType!: string | null;

	/** License plate issuing state — complements the existing licensePlate. */
	@Column('varchar', { name: 'licensePlateState', length: 8, nullable: true })
	public licensePlateState!: string | null;

	// Live telemetry snapshot, written by the Motive poller alongside the
	// existing currentLatitude/Longitude/At/Source fields (source='motive').
	// These are high-churn machine writes and are intentionally excluded
	// from auditing (see MotiveSyncService — unauditedUpdate).

	/** Direction of travel in degrees (0 = north). Null when unknown/stopped. */
	@Column('decimal', {
		name: 'currentBearing',
		precision: 6,
		scale: 2,
		nullable: true,
	})
	public currentBearing!: string | null;

	/** Current speed in mph as reported by the telematics device. */
	@Column('decimal', {
		name: 'currentSpeed',
		precision: 6,
		scale: 2,
		nullable: true,
	})
	public currentSpeed!: string | null;

	/** Vehicle state from telematics: moving / idling / off. */
	@Column('varchar', {
		name: 'currentVehicleState',
		length: 16,
		nullable: true,
	})
	public currentVehicleState!: string | null;

	/** Odometer reading (miles) from telematics. */
	@Column('decimal', {
		name: 'currentOdometer',
		precision: 12,
		scale: 2,
		nullable: true,
	})
	public currentOdometer!: string | null;

	/**
	 * PD-16793 — when `currentOdometer` was read (Motive's fix time). Needed
	 * because `currentLocationAt` is also written by driver-device GPS, and
	 * `motiveSyncedAt` is the poll time, so neither says how old the
	 * odometer value is. The sync leaves both columns untouched when a fix
	 * carries no odometer (engine off), so the last real reading survives.
	 */
	@Column('datetime', { name: 'currentOdometerAt', nullable: true })
	public currentOdometerAt!: Date | null;

	/** Lifetime engine hours from telematics. */
	@Column('decimal', {
		name: 'currentEngineHours',
		precision: 10,
		scale: 2,
		nullable: true,
	})
	public currentEngineHours!: string | null;

	/** Primary fuel tank remaining, 0-100 (%). */
	@Column('decimal', {
		name: 'currentFuelPercent',
		precision: 5,
		scale: 2,
		nullable: true,
	})
	public currentFuelPercent!: string | null;

	/**
	 * Reverse-geocoded street address of the current position, as provided
	 * by Motive — lets the UI show "1985 Lakeland, Sylvan Lake, MI" without
	 * spending a geocoding call.
	 */
	@Column('varchar', {
		name: 'currentLocationDescription',
		length: 255,
		nullable: true,
	})
	public currentLocationDescription!: string | null;
}
